<template>
    <div class="event-actions">
        <template v-if="!isPhone">
            <BTN v-if="deletable" icon_left="brand_trash" type="warn" bh="48" @handle="remove">{{ langJson('Delete event') }}</BTN>
            <BTN icon_left="eye" type="detail" bh="48" @handle="preview">{{ langJson('Preview') }}</BTN>
            <BTN v-if="canWithdraw" type="detail" bh="48" :load="busy" @handle="withdraw">{{ langJson('Withdraw from review') }}</BTN>
            <el-tooltip v-if="canSubmit" :disabled="ready" :content="langJson('Complete Basics and Ticketing to submit')" placement="bottom">
                <span><BTN type="active" bh="48" :disable="!ready" :load="busy" @handle="submit">{{ langJson('Submit for review') }}</BTN></span>
            </el-tooltip>
            <BTN v-if="canEnd" type="warn" bh="48" :load="busy" @handle="end">{{ langJson('End event') }}</BTN>
            <el-tooltip v-if="canLaunch" :disabled="stripeReady" :content="langJson('Connect Stripe before launching')" placement="bottom">
                <span><BTN type="active" bh="48" :disable="!stripeReady" :load="busy || launching" @handle="launch">{{ langJson(launching ? 'Launching…' : 'Launch event') }}</BTN></span>
            </el-tooltip>
        </template>
        <BtnPop v-else ref="pop" :left="-200">
            <div slot="target" class="event-actions__more"><GI ws="24" hs="24" name="brand_more"></GI></div>
            <div slot="main" class="event-actions__menu" @click="$refs.pop.closePopup()">
                <div class="event-actions__item" @click="preview"><GI ws="20" hs="20" name="brand_eye"></GI>{{ langJson('Preview') }}</div>
                <div v-if="canSubmit" :class="['event-actions__item', !ready && 'event-actions__item--disabled']" @click="ready && submit()">{{ langJson('Submit for review') }}</div>
                <div v-if="canWithdraw" class="event-actions__item" @click="withdraw">{{ langJson('Withdraw from review') }}</div>
                <div v-if="canLaunch" :class="['event-actions__item', (!stripeReady || launching) && 'event-actions__item--disabled']" @click="stripeReady && !launching && launch()">{{ langJson(launching ? 'Launching…' : 'Launch event') }}</div>
                <div v-if="canEnd" class="event-actions__item event-actions__item--danger" @click="end">{{ langJson('End event') }}</div>
                <div v-if="deletable" class="event-actions__item event-actions__item--danger" @click="remove"><GI ws="20" hs="20" name="brand_trash"></GI>{{ langJson('Delete event') }}</div>
            </div>
        </BtnPop>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { apiErrorMessage, navigate } from '@/assets/js/organizerEvent'

/** Header actions shared by the event Overview and step editor. */
export default {
    props: {
        event: { type: Object, required: true },
        // Awaited before submitting — the editor flushes unsaved autosave changes here.
        beforeSubmit: { type: Function, default: null },
    },
    data() {
        return { busy: false, pollTimer: null }
    },
    computed: {
        deletable() {
            return ['draft', 'rejected'].includes(this.event.status)
        },
        canSubmit() {
            return ['draft', 'rejected'].includes(this.event.status)
        },
        canWithdraw() {
            return this.event.status === 'in_review'
        },
        canEnd() {
            return this.event.status === 'live'
        },
        canLaunch() {
            return this.event.status === 'approved'
        },
        launching() {
            return this.event.deployStatus === 'deploying'
        },
        stripeReady() {
            return Boolean(this.event.stripe && this.event.stripe.chargesEnabled)
        },
        ready() {
            const checklist = this.event.checklist || {}
            return Boolean(checklist.basics && checklist.ticketing)
        },
    },
    watch: {
        launching: {
            immediate: true,
            handler(on) {
                if (on) this.pollLaunch()
            },
        },
    },
    beforeDestroy() {
        clearTimeout(this.pollTimer)
    },
    methods: {
        async end() {
            try {
                await this.$confirm(
                    this.langJson('Ticket sales stop straight away. Check-in keeps working, so people who already have tickets can still get in. This can’t be undone.'),
                    this.langJson('End this event?'),
                    { confirmButtonText: this.langJson('End event'), cancelButtonText: this.langJson('Cancel'), type: 'warning' },
                )
            } catch (e) {
                return
            }
            this.busy = true
            try {
                const res = await organizerApi.endEvent(this.event.id)
                this.pushMsg('success', this.langJson('Event ended — ticket sales are closed'))
                this.$emit('changed', res.data)
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not end the event')))
            }
            this.busy = false
        },
        async launch() {
            try {
                await this.$confirm(
                    this.langJson('We’ll create your event’s ticket contract on-chain and open ticket sales. This takes about a minute. Ticket categories can’t be changed after launch.'),
                    this.langJson('Launch your event?'),
                    { confirmButtonText: this.langJson('Launch'), cancelButtonText: this.langJson('Cancel') },
                )
            } catch (e) {
                return
            }
            this.busy = true
            try {
                const res = await organizerApi.launchEvent(this.event.id)
                this.$emit('changed', res.data)
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not launch the event')))
            }
            this.busy = false
        },
        // The contract deploys in the background; check back until it settles.
        pollLaunch() {
            clearTimeout(this.pollTimer)
            this.pollTimer = setTimeout(async () => {
                try {
                    const res = await organizerApi.getEvent(this.event.id)
                    const event = res.data
                    if (event.deployStatus === 'deploying') return this.pollLaunch()
                    if (event.status === 'live') this.pushMsg('success', this.langJson('Your event is live — tickets are on sale'))
                    else if (event.deployStatus === 'failed') this.pushMsg('error', this.langJson('Launch failed — see the details on the overview'))
                    this.$emit('changed', event)
                } catch (err) {
                    this.pollLaunch()
                }
            }, 3000)
        },
        async submit() {
            try {
                await this.$confirm(
                    this.langJson('We’ll check it follows our rules and guidelines — please allow 1 business day. You can’t edit it while it’s in review, but you can withdraw it.'),
                    this.langJson('Submit for review?'),
                    { confirmButtonText: this.langJson('Submit'), cancelButtonText: this.langJson('Cancel') },
                )
            } catch (e) {
                return
            }
            this.busy = true
            try {
                if (this.beforeSubmit) await this.beforeSubmit()
                const res = await organizerApi.submitEvent(this.event.id)
                this.pushMsg('success', this.langJson('Submitted for review'))
                this.$emit('changed', res.data)
                if (this.$route.path !== '/brand/events/overview') navigate(this, { path: '/brand/events/overview', query: { id: this.event.id } })
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not submit the event')))
            }
            this.busy = false
        },
        async withdraw() {
            this.busy = true
            try {
                const res = await organizerApi.withdrawEvent(this.event.id)
                this.pushMsg('success', this.langJson('Withdrawn — you can edit your event again'))
                this.$emit('changed', res.data)
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not withdraw the event')))
            }
            this.busy = false
        },
        preview() {
            this.$router.push({ path: '/brand/events/preview', query: { id: this.event.id } })
        },
        async remove() {
            try {
                await this.$confirm(this.langJson('This permanently deletes the draft.'), `${this.langJson('Delete')} “${this.event.title}”?`, {
                    confirmButtonText: this.langJson('Delete'),
                    cancelButtonText: this.langJson('Cancel'),
                    type: 'warning',
                })
            } catch (e) {
                return
            }
            try {
                await organizerApi.deleteEvent(this.event.id)
                this.pushMsg('success', this.langJson('Event deleted'))
                this.$router.replace({ path: '/brand/events/index', query: { tab: 'draft' } })
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not delete the event')))
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.event-actions {
    display: flex;
    align-items: center;
    gap: 16px;

    &__more {
        width: 36px;
        height: 36px;
        border: 1px solid var(--border_color);
        border-radius: 26px;
        background: var(--color_white);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-sizing: border-box;
    }
    &__menu {
        padding: 24px;
        background: var(--color_white);
        border: 1px solid var(--border_color);
        border-radius: 24px;
        box-shadow: 4px 8px 24px rgba(187, 196, 221, 0.08);
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
    &__item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: 'Lato';
        font-size: 14px;
        white-space: nowrap;
        color: var(--font_deep_color);
        cursor: pointer;

        &--danger {
            color: var(--danger_color);
        }
        &--disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }
}
</style>
